import {
  NotificationConfig,
  NotificationTypeMetadata,
  SystemInstance,
  useNotificationTypes,
  useTenantConfig,
} from '@/api/hooks/useTenantConfig'
import { SeverityTag } from '@/components/Display/SeverityDisplay'
import { DownOutlined, RightOutlined } from '@ant-design/icons'
import { Checkbox, Input, Modal, Select, Switch, Typography, theme } from 'antd'
import { useEffect, useMemo, useState } from 'react'

const { Text } = Typography

type NotificationType = NonNullable<
  NotificationConfig['enabled_notifications']
>[number]

type LinkedPartner = NonNullable<NotificationConfig['partners']>[number]

// Select values must be primitives, so partners round-trip through a composite key
const partnerValue = (partner: LinkedPartner) =>
  `${partner.tenant_id}::${partner.partner_instance_id}`

const partnerFromValue = (value: string): LinkedPartner => {
  const separator = value.indexOf('::')
  return {
    tenant_id: value.slice(0, separator),
    partner_instance_id: value.slice(separator + 2),
  }
}

interface NotificationConfigModalProps {
  open: boolean
  onClose: () => void
  instance: SystemInstance | null
  onSave: (config: NotificationConfig) => void
  loading?: boolean
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const CATEGORY_DESCRIPTIONS: Record<string, string> = {
  'Error Notifications': 'Errors processing events in Crossroads',
  'Order Activity': 'New orders, modifications, and delivery updates',
  'Change Requests': 'Requests, approvals, and rejections',
}

// Merge Order Progression into Order Activity
const CATEGORY_MERGE: Record<string, string> = {
  'Order Progression': 'Order Activity',
}

// Only the error category splits into panels: Issues on the left, then Checkpoints
// and Warnings stacked on the right.
const ERROR_CATEGORY = 'Error Notifications'
const CHECKPOINT_KEY_PREFIX = 'checkpoint_'
const WARNING_KEY_PREFIX = 'warning_'

interface NotificationCheckboxRowProps {
  type: NotificationTypeMetadata
  checked: boolean
  onChange: (checked: boolean) => void
}

function NotificationCheckboxRow({
  type,
  checked,
  onChange,
}: NotificationCheckboxRowProps) {
  const { token } = theme.useToken()

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 8,
        padding: '4px 0',
        borderRadius: 4,
        transition: 'background-color 0.15s',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = token.colorFillSecondary
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = 'transparent'
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', minWidth: 0 }}>
        <Checkbox
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          style={{ marginTop: 2 }}
        />
        <div
          style={{ marginLeft: 8, cursor: 'pointer' }}
          onClick={() => onChange(!checked)}
        >
          <Text style={{ fontSize: 13, lineHeight: 1.3 }}>{type.name}</Text>
          {type.description && (
            <Text
              type='secondary'
              style={{ fontSize: 11, display: 'block', lineHeight: 1.3 }}
            >
              {type.description}
            </Text>
          )}
        </div>
      </div>
      <SeverityTag severity={type.severity} style={{ marginInlineEnd: 0 }} />
    </div>
  )
}

function PanelHeader({ children }: { children: string }) {
  return (
    <Text
      type='secondary'
      style={{
        display: 'block',
        fontSize: 11,
        fontWeight: 500,
        letterSpacing: 0.5,
        textTransform: 'uppercase',
        marginBottom: 8,
      }}
    >
      {children}
    </Text>
  )
}

export function NotificationConfigModal({
  open,
  onClose,
  instance,
  onSave,
  loading,
}: NotificationConfigModalProps) {
  const { token } = theme.useToken()
  const { data: notificationTypes } = useNotificationTypes()
  const { data: tenantConfig } = useTenantConfig()

  const [emails, setEmails] = useState<string[]>([])
  const [partnerValues, setPartnerValues] = useState<string[]>([])
  const [enabledNotifications, setEnabledNotifications] = useState<
    NotificationType[]
  >([])
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set(),
  )
  const [supportEmail, setSupportEmail] = useState<string>('')
  const [supportPhone, setSupportPhone] = useState<string>('')

  useEffect(() => {
    if (instance) {
      setEmails(instance.notification_config?.emails || [])
      setEnabledNotifications(
        instance.notification_config?.enabled_notifications || [],
      )
      setSupportEmail(instance.notification_config?.support_email || '')
      setSupportPhone(instance.notification_config?.support_phone || '')
      setPartnerValues(
        (instance.notification_config?.partners || []).map(partnerValue),
      )
    }
  }, [instance])

  // Reset expanded state when modal closes
  useEffect(() => {
    if (!open) {
      setExpandedCategories(new Set())
    }
  }, [open])

  const groupedTypes = useMemo(() => {
    if (!notificationTypes) return {}
    return notificationTypes.reduce(
      (acc, type) => {
        // Apply category merging
        const category = CATEGORY_MERGE[type.category] || type.category
        if (!acc[category]) {
          acc[category] = []
        }
        acc[category].push(type)
        return acc
      },
      {} as Record<string, NotificationTypeMetadata[]>,
    )
  }, [notificationTypes])

  // Partners are linked to a specific instance, so only this instance's are selectable
  const partnerOptions = useMemo(() => {
    const available = (tenantConfig?.partners || [])
      .filter((p) => p.instance_id === instance?.id)
      .map((p) => ({
        value: partnerValue(p),
        label: `${p.tenant_name} / ${p.partner_instance_name}`,
      }))
    const known = new Set(available.map((o) => o.value))
    // Keep already-saved partners selectable (and removable) even if they've been unlinked
    const orphaned = partnerValues
      .filter((v) => !known.has(v))
      .map((v) => ({ value: v, label: `Unknown partner (${v})` }))
    return [...available, ...orphaned]
  }, [tenantConfig?.partners, instance?.id, partnerValues])

  const handleSave = () => {
    onSave({
      emails,
      enabled_notifications: enabledNotifications,
      support_email: supportEmail || null,
      support_phone: supportPhone || null,
      partners: partnerValues.map(partnerFromValue),
    })
  }

  const handleNotificationChange = (
    key: NotificationType,
    checked: boolean,
  ) => {
    if (checked) {
      setEnabledNotifications([...enabledNotifications, key])
    } else {
      setEnabledNotifications(enabledNotifications.filter((k) => k !== key))
    }
  }

  const isCategoryEnabled = (types: NotificationTypeMetadata[]) => {
    return types.some((type) =>
      enabledNotifications.includes(type.name as NotificationType),
    )
  }

  const handleCategoryToggle = (
    category: string,
    types: NotificationTypeMetadata[],
    enabled: boolean,
  ) => {
    const typeKeys = types.map((t) => t.name as NotificationType)
    if (enabled) {
      // Enable all types in category
      const newEnabled = [...enabledNotifications]
      for (const key of typeKeys) {
        if (!newEnabled.includes(key)) {
          newEnabled.push(key)
        }
      }
      setEnabledNotifications(newEnabled)
    } else {
      // Disable all types in category and collapse
      setEnabledNotifications(
        enabledNotifications.filter((k) => !typeKeys.includes(k)),
      )
      setExpandedCategories((prev) => {
        const next = new Set(prev)
        next.delete(category)
        return next
      })
    }
  }

  const renderTypeRow = (type: NotificationTypeMetadata) => {
    const typeKey = type.name as NotificationType
    return (
      <NotificationCheckboxRow
        key={type.key}
        type={type}
        checked={enabledNotifications.includes(typeKey)}
        onChange={(checked) => handleNotificationChange(typeKey, checked)}
      />
    )
  }

  const toggleExpanded = (category: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev)
      if (next.has(category)) {
        next.delete(category)
      } else {
        next.add(category)
      }
      return next
    })
  }

  const validateEmail = (email: string) => EMAIL_REGEX.test(email)

  return (
    <Modal
      title={`Notification Settings - ${instance?.name || instance?.system_id || ''}`}
      open={open}
      onOk={handleSave}
      onCancel={onClose}
      okText='Save'
      confirmLoading={loading}
      width={640}
    >
      <div style={{ marginBottom: 20 }}>
        <Text strong>Email Recipients</Text>
        <Select
          mode='tags'
          style={{ width: '100%', marginTop: 8 }}
          placeholder='Type email and press Enter'
          value={emails}
          onChange={(values) => {
            const validEmails = values.filter(validateEmail)
            setEmails(validEmails)
          }}
          tokenSeparators={[',', ' ']}
          suffixIcon={null}
          notFoundContent={null}
        />
        <Text type='secondary' style={{ fontSize: 11 }}>
          Press Enter or comma to add multiple addresses
        </Text>
      </div>

      <div style={{ marginBottom: 20 }}>
        <Text strong>Support Contact</Text>
        <Text
          type='secondary'
          style={{ fontSize: 11, display: 'block', marginBottom: 8 }}
        >
          Displayed in delivery notification emails
        </Text>
        <div style={{ display: 'flex', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <Text type='secondary' style={{ fontSize: 12 }}>
              Email
            </Text>
            <Input
              placeholder='support@example.com'
              value={supportEmail}
              onChange={(e) => setSupportEmail(e.target.value)}
              style={{ marginTop: 4 }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <Text type='secondary' style={{ fontSize: 12 }}>
              Phone
            </Text>
            <Input
              placeholder='(555) 123-4567'
              value={supportPhone}
              onChange={(e) => setSupportPhone(e.target.value)}
              style={{ marginTop: 4 }}
            />
          </div>
        </div>
      </div>

      <div style={{ marginBottom: 20 }}>
        <Text strong>Partners</Text>
        <Text
          type='secondary'
          style={{ fontSize: 11, display: 'block', marginBottom: 8 }}
        >
          Applies to every notification group below
        </Text>
        <Select
          mode='multiple'
          style={{ width: '100%' }}
          placeholder='Search partners'
          value={partnerValues}
          onChange={setPartnerValues}
          options={partnerOptions}
          optionFilterProp='label'
          suffixIcon={null}
        />
        <Text type='secondary' style={{ fontSize: 11 }}>
          Select the partners you want notifications for. None selected means no
          emails.
        </Text>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {Object.entries(groupedTypes).map(([category, types]) => {
          const isEnabled = isCategoryEnabled(types)
          const isExpanded = expandedCategories.has(category)
          const description = CATEGORY_DESCRIPTIONS[category] || ''

          return (
            <div
              key={category}
              style={{
                borderRadius: 8,
                border: `1px solid ${token.colorBorderSecondary}`,
                overflow: 'hidden',
              }}
            >
              {/* Category Header */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '12px 16px',
                  backgroundColor: token.colorFillQuaternary,
                  gap: 12,
                }}
              >
                <div
                  style={{
                    flex: 1,
                    minWidth: 0,
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 8,
                    cursor: isEnabled ? 'pointer' : 'default',
                  }}
                  onClick={() => isEnabled && toggleExpanded(category)}
                >
                  {isEnabled && (
                    <span
                      style={{ marginTop: 2, color: token.colorTextSecondary }}
                    >
                      {isExpanded ? (
                        <DownOutlined style={{ fontSize: 10 }} />
                      ) : (
                        <RightOutlined style={{ fontSize: 10 }} />
                      )}
                    </span>
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Text strong style={{ display: 'block' }}>
                      {category}
                    </Text>
                    <Text type='secondary' style={{ fontSize: 12 }}>
                      {description}
                    </Text>
                  </div>
                </div>
                <Switch
                  checked={isEnabled}
                  onChange={(checked) =>
                    handleCategoryToggle(category, types, checked)
                  }
                  size='small'
                />
              </div>

              {/* Expanded Sub-options */}
              {isEnabled && isExpanded && (
                <div
                  style={{
                    padding: '4px 16px 6px 16px',
                    borderTop: `1px solid ${token.colorBorderSecondary}`,
                    backgroundColor: token.colorFillAlter,
                  }}
                >
                  {category === ERROR_CATEGORY ? (
                    <div
                      style={{
                        display: 'flex',
                        margin: '10px 0',
                        border: `1px solid ${token.colorBorderSecondary}`,
                        borderRadius: 8,
                        backgroundColor: token.colorBgContainer,
                      }}
                    >
                      <div
                        style={{ flex: 1, minWidth: 0, padding: '12px 16px' }}
                      >
                        <PanelHeader>Issues</PanelHeader>
                        {types
                          .filter(
                            (t) =>
                              !t.key.startsWith(CHECKPOINT_KEY_PREFIX) &&
                              !t.key.startsWith(WARNING_KEY_PREFIX),
                          )
                          .map(renderTypeRow)}
                      </div>
                      <div
                        style={{
                          flex: 1,
                          minWidth: 0,
                          borderLeft: `1px solid ${token.colorBorderSecondary}`,
                        }}
                      >
                        <div style={{ padding: '12px 16px' }}>
                          <PanelHeader>Checkpoints</PanelHeader>
                          {types
                            .filter((t) =>
                              t.key.startsWith(CHECKPOINT_KEY_PREFIX),
                            )
                            .map(renderTypeRow)}
                        </div>
                        <div
                          style={{
                            padding: '12px 16px',
                            borderTop: `1px solid ${token.colorBorderSecondary}`,
                          }}
                        >
                          <PanelHeader>Warnings</PanelHeader>
                          {types
                            .filter((t) => t.key.startsWith(WARNING_KEY_PREFIX))
                            .map(renderTypeRow)}
                        </div>
                      </div>
                    </div>
                  ) : (
                    types.map(renderTypeRow)
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>

      {(!notificationTypes || notificationTypes.length === 0) && (
        <Text type='secondary'>No notification types available</Text>
      )}
    </Modal>
  )
}
